<script setup lang="ts">
import { computed, onBeforeUnmount, ref, shallowRef, watch, type Component } from 'vue'
import type EventBase from '@/model/EventClass/EventBase'
import EventEditorBlank from '@/components/eventEditors/EventEditorBlank.vue'
import { hasEventEditor, resolveEventEditor } from '@/components/eventEditors/resolveEditor'
import { useSceneEditorStore } from '@/store/sceneEditor'

const props = defineProps<{
  event: EventBase | null
}>()

const editor = useSceneEditorStore()
const Resolved = shallowRef<Component>(EventEditorBlank)
const labelWidth = ref(72)
const dragging = ref(false)
const hasSupportedEditor = ref(false)

watch(
  () => props.event?.Code,
  (code) => {
    hasSupportedEditor.value = code != null && hasEventEditor(Number(code))
    Resolved.value = code == null ? EventEditorBlank : resolveEventEditor(Number(code))
  },
  { immediate: true },
)

const comp = computed(() => Resolved.value)
const shellVars = computed(() => `--event-prop-label-w:${labelWidth.value}px;`)
const eventRenderRevision = computed(() => editor.sceneEventsRevision)

let stopDrag: (() => void) | null = null

function startDividerDrag(event: PointerEvent) {
  event.preventDefault()
  const startX = event.clientX
  const startWidth = labelWidth.value

  const onMove = (moveEvent: PointerEvent) => {
    const nextWidth = Math.min(120, Math.max(52, startWidth + moveEvent.clientX - startX))
    labelWidth.value = nextWidth
  }

  const onUp = () => {
    window.removeEventListener('pointermove', onMove)
    window.removeEventListener('pointerup', onUp)
    window.removeEventListener('pointercancel', onUp)
    dragging.value = false
    stopDrag = null
  }

  dragging.value = true
  window.addEventListener('pointermove', onMove)
  window.addEventListener('pointerup', onUp)
  window.addEventListener('pointercancel', onUp)
  stopDrag = onUp
}

onBeforeUnmount(() => {
  stopDrag?.()
})

function touchEventRevision() {
  editor.notifySceneEventValueChanged()
}

function handlePropertyChange() {
  touchEventRevision()
  editor.commitEventPropertyHistory()
}
</script>

<template>
  <div v-if="event && hasSupportedEditor" class="event-editor-shell" :class="{ 'event-editor-shell--dragging': dragging }" :style="shellVars">
    <div class="event-editor-divider" aria-hidden="true" />
    <button
      type="button"
      class="event-editor-divider-handle"
      aria-label="调整属性名称列宽"
      @pointerdown="startDividerDrag"
    />
    <div
      @focusin="editor.beginEventPropertyHistory"
      @focusout="editor.commitEventPropertyHistory"
      @input="touchEventRevision"
      @change="handlePropertyChange"
    >
      <component :is="comp" :event="event" :revision="eventRenderRevision" />
    </div>
  </div>
</template>

<style scoped>
.event-editor-shell {
  position: relative;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  min-height: 0;
}

.event-editor-divider {
  position: absolute;
  top: 0;
  bottom: 0;
  left: calc(10px + var(--event-prop-label-w) + 5px);
  width: 1px;
  background: var(--theme-editor-divider);
  pointer-events: none;
  z-index: 1;
}

.event-editor-divider-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  left: calc(10px + var(--event-prop-label-w));
  width: 12px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: col-resize;
  z-index: 2;
}

.event-editor-shell--dragging .event-editor-divider {
  background: var(--theme-accent);
}

.event-editor-shell--dragging .event-editor-divider-handle {
  cursor: col-resize;
}

</style>
